import React, { Fragment, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { AlertCircle, Info, TriangleAlert, User, Users, Video } from "lucide-react";
import { cn } from "../../utils";

type WikiPerson = {
  id: string;
  name?: string;
  email?: string;
  title?: string;
  roleTitle?: string;
  department?: string;
  photoUrl?: string;
  status?: string;
};

type ParsedProps = Record<string, string>;

type WikiRendererProps = {
  content: string;
  people?: WikiPerson[];
  pageSlugMap?: Record<string, string>;
  onNavigateToPage?: (slug: string) => void;
  className?: string;
};

type RenderBlock =
  | { type: "markdown"; content: string }
  | { type: "table"; lines: string[] }
  | { type: "callout"; props: ParsedProps; content: string }
  | { type: "image"; props: ParsedProps }
  | { type: "video"; props: ParsedProps }
  | { type: "gallery"; items: Array<{ src: string; caption?: string }> }
  | { type: "peopleGrid"; props: ParsedProps }
  | { type: "columns"; columns: Array<{ props: ParsedProps; content: string }> }
  | { type: "cards"; cards: Array<{ props: ParsedProps; content: string }> };

function parseDirectiveProps(source?: string) {
  const props: ParsedProps = {};
  if (!source) return props;
  const regex = /(\w+)="([^"]*)"/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(source))) {
    props[match[1]] = match[2];
  }
  return props;
}

function detectMarkdownTable(lines: string[], startIndex: number) {
  const header = lines[startIndex];
  const divider = lines[startIndex + 1];
  if (!header || !divider) return null;
  if (!header.includes("|")) return null;
  if (!/^\s*\|?[\s:-|]+\|?\s*$/.test(divider)) return null;

  const collected: string[] = [header, divider];
  let index = startIndex + 2;
  while (index < lines.length && lines[index].includes("|")) {
    collected.push(lines[index]);
    index += 1;
  }
  return {
    block: { type: "table", lines: collected } as RenderBlock,
    nextIndex: index,
  };
}

function parseGalleryItems(blockLines: string[]) {
  return blockLines
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [src, caption] = line.split("|").map((part) => part.trim());
      return { src, caption };
    })
    .filter((item) => item.src);
}

function parseNestedItems(
  lines: string[],
  startIndex: number,
  containerDirective: string,
  itemDirective: string
) {
  const items: Array<{ props: ParsedProps; content: string }> = [];
  let index = startIndex + 1;

  while (index < lines.length) {
    const line = lines[index];
    if (line.trim() === "::") {
      return { items, nextIndex: index + 1 };
    }

    const itemMatch = line.match(new RegExp(`^:::${itemDirective}(?:\\{([^}]*)\\})?\\s*$`));
    if (!itemMatch) {
      index += 1;
      continue;
    }

    const itemProps = parseDirectiveProps(itemMatch[1]);
    const contentLines: string[] = [];
    index += 1;
    while (index < lines.length && lines[index].trim() !== ":::") {
      contentLines.push(lines[index]);
      index += 1;
    }
    items.push({
      props: itemProps,
      content: contentLines.join("\n").trim(),
    });
    if (lines[index]?.trim() === ":::") {
      index += 1;
    }
  }

  return { items, nextIndex: index };
}

function parseWikiContent(content: string): RenderBlock[] {
  const lines = String(content || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const blocks: RenderBlock[] = [];
  let markdownBuffer: string[] = [];

  const flushMarkdown = () => {
    const nextContent = markdownBuffer.join("\n").trim();
    if (nextContent) {
      blocks.push({ type: "markdown", content: nextContent });
    }
    markdownBuffer = [];
  };

  for (let index = 0; index < lines.length; ) {
    const line = lines[index];
    const trimmed = line.trim();

    const tableBlock = detectMarkdownTable(lines, index);
    if (tableBlock) {
      flushMarkdown();
      blocks.push(tableBlock.block);
      index = tableBlock.nextIndex;
      continue;
    }

    const singleLineDirective = trimmed.match(/^::(image|video|peopleGrid)(?:\{([^}]*)\})?\s*$/);
    if (singleLineDirective) {
      flushMarkdown();
      const type = singleLineDirective[1];
      const props = parseDirectiveProps(singleLineDirective[2]);
      blocks.push({ type: type as RenderBlock["type"], props } as RenderBlock);
      index += 1;
      continue;
    }

    const blockDirective = trimmed.match(/^::(callout)(?:\{([^}]*)\})?\s*$/);
    if (blockDirective) {
      flushMarkdown();
      const props = parseDirectiveProps(blockDirective[2]);
      const contentLines: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== "::") {
        contentLines.push(lines[index]);
        index += 1;
      }
      blocks.push({
        type: "callout",
        props,
        content: contentLines.join("\n").trim(),
      });
      index += 1;
      continue;
    }

    if (trimmed === "::gallery") {
      flushMarkdown();
      const contentLines: string[] = [];
      index += 1;
      while (index < lines.length && lines[index].trim() !== "::") {
        contentLines.push(lines[index]);
        index += 1;
      }
      blocks.push({
        type: "gallery",
        items: parseGalleryItems(contentLines),
      });
      index += 1;
      continue;
    }

    if (trimmed === "::columns") {
      flushMarkdown();
      const { items, nextIndex } = parseNestedItems(lines, index, "columns", "column");
      blocks.push({
        type: "columns",
        columns: items,
      });
      index = nextIndex;
      continue;
    }

    if (trimmed === "::cards") {
      flushMarkdown();
      const { items, nextIndex } = parseNestedItems(lines, index, "cards", "card");
      blocks.push({
        type: "cards",
        cards: items,
      });
      index = nextIndex;
      continue;
    }

    markdownBuffer.push(line);
    index += 1;
  }

  flushMarkdown();
  return blocks;
}

function formatWikiTableLine(line: string) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function isExternalVideoUrl(src: string) {
  return /youtube\.com|youtu\.be|vimeo\.com|drive\.google\.com/.test(src);
}

function buildEmbeddedVideoUrl(src: string) {
  if (src.includes("youtu.be/")) {
    const id = src.split("youtu.be/")[1]?.split(/[?&]/)[0];
    return id ? `https://www.youtube.com/embed/${id}` : src;
  }
  if (src.includes("youtube.com/watch")) {
    const url = new URL(src);
    const id = url.searchParams.get("v");
    return id ? `https://www.youtube.com/embed/${id}` : src;
  }
  if (src.includes("vimeo.com/")) {
    const id = src.split("vimeo.com/")[1]?.split(/[?&/]/)[0];
    return id ? `https://player.vimeo.com/video/${id}` : src;
  }
  if (src.includes("drive.google.com/file/d/")) {
    const id = src.split("drive.google.com/file/d/")[1]?.split("/")[0];
    return id ? `https://drive.google.com/file/d/${id}/preview` : src;
  }
  return src;
}

function getPeopleGridEntries(props: ParsedProps, people: WikiPerson[]) {
  const department = String(props.department || "").trim().toLowerCase();
  const ids = String(props.ids || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  return people.filter((person) => {
    if (ids.length > 0) return ids.includes(person.id);
    if (department) return String(person.department || "").trim().toLowerCase() === department;
    return true;
  });
}

function renderMarkdownChunk(
  content: string,
  pageSlugMap: Record<string, string>,
  onNavigateToPage?: (slug: string) => void
) {
  return (
    <ReactMarkdown
      components={{
        a: ({ href, children }) => {
          const value = String(href || "");
          if (value.startsWith("wiki:")) {
            const slug = value.replace(/^wiki:/, "").trim();
            return (
              <button
                type="button"
                className="font-medium text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700"
                onClick={() => onNavigateToPage?.(slug)}
              >
                {children}
              </button>
            );
          }
          if (value.startsWith("/wiki/")) {
            const slug = value.replace(/^\/wiki\//, "").trim();
            return (
              <button
                type="button"
                className="font-medium text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700"
                onClick={() => onNavigateToPage?.(slug)}
              >
                {children}
              </button>
            );
          }
          const slug = pageSlugMap[value];
          if (slug) {
            return (
              <button
                type="button"
                className="font-medium text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700"
                onClick={() => onNavigateToPage?.(slug)}
              >
                {children}
              </button>
            );
          }
          return (
            <a
              href={value}
              target={value.startsWith("http") ? "_blank" : undefined}
              rel={value.startsWith("http") ? "noreferrer" : undefined}
              className="font-medium text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700"
            >
              {children}
            </a>
          );
        },
        img: ({ src, alt }) => (
          <img
            src={String(src || "")}
            alt={String(alt || "")}
            className="my-4 max-w-full rounded-md border border-slate-200"
          />
        ),
        hr: () => <hr className="my-6 border-slate-200" />,
        blockquote: ({ children }) => (
          <blockquote className="my-4 rounded-r-lg border-l-4 border-indigo-300 bg-indigo-50 px-4 py-3 text-slate-700">
            {children}
          </blockquote>
        ),
        code: ({ children }) => (
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[0.92em] text-slate-800">{children}</code>
        ),
        pre: ({ children }) => (
          <pre className="my-4 overflow-x-auto rounded-md border border-slate-200 bg-slate-950 px-4 py-3 text-sm text-slate-100">
            {children}
          </pre>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

export function WikiRenderer({
  content,
  people = [],
  pageSlugMap = {},
  onNavigateToPage,
  className,
}: WikiRendererProps) {
  const blocks = useMemo(() => parseWikiContent(content), [content]);

  return (
    <div className={cn("wiki-markdown prose prose-slate max-w-none text-slate-700", className)}>
      {blocks.map((block, index) => {
        if (block.type === "markdown") {
          return <Fragment key={`markdown-${index}`}>{renderMarkdownChunk(block.content, pageSlugMap, onNavigateToPage)}</Fragment>;
        }

        if (block.type === "table") {
          const rows = block.lines.map(formatWikiTableLine);
          const [headings, , ...body] = rows;
          return (
            <div key={`table-${index}`} className="my-6 overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {headings.map((heading, headingIndex) => (
                      <th key={headingIndex} className="border-b border-slate-200 px-4 py-2 text-left font-semibold text-slate-900">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.map((row, rowIndex) => (
                    <tr key={rowIndex} className="odd:bg-white even:bg-slate-50/40">
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="border-b border-slate-100 px-4 py-2 align-top text-slate-700">
                          {renderMarkdownChunk(cell, pageSlugMap, onNavigateToPage)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        if (block.type === "callout") {
          const type = String(block.props.type || "info").toLowerCase();
          const title = block.props.title;
          const tone =
            type === "warning"
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : type === "danger"
                ? "border-rose-300 bg-rose-50 text-rose-900"
                : "border-sky-300 bg-sky-50 text-sky-900";
          const Icon = type === "warning" ? TriangleAlert : type === "danger" ? AlertCircle : Info;
          return (
            <div key={`callout-${index}`} className={cn("my-6 rounded-lg border px-4 py-4", tone)}>
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  {title ? <p className="mb-2 text-sm font-semibold">{title}</p> : null}
                  {renderMarkdownChunk(block.content, pageSlugMap, onNavigateToPage)}
                </div>
              </div>
            </div>
          );
        }

        if (block.type === "image") {
          const align = String(block.props.align || "center");
          const width = String(block.props.width || "full");
          const src = String(block.props.src || "");
          const alt = String(block.props.alt || "");
          const caption = String(block.props.caption || "");
          const widthClass =
            width === "inline"
              ? "max-w-[220px]"
              : width === "sm"
                ? "max-w-sm"
                : width === "md"
                  ? "max-w-2xl"
                  : width === "lg"
                    ? "max-w-4xl"
                    : "max-w-full";
          const justifyClass =
            align === "left"
              ? "justify-start"
              : align === "right"
                ? "justify-end"
                : align === "inline"
                  ? "justify-start"
                  : "justify-center";
          return (
            <figure key={`image-${index}`} className={cn("my-6 flex", justifyClass)}>
              <div className={cn("w-full", widthClass)}>
                <img src={src} alt={alt} className="w-full rounded-lg border border-slate-200 bg-white object-cover shadow-sm" />
                {caption ? <figcaption className="mt-2 text-center text-sm text-slate-500">{caption}</figcaption> : null}
              </div>
            </figure>
          );
        }

        if (block.type === "video") {
          const src = String(block.props.src || "");
          const title = String(block.props.title || "Embedded video");
          if (isExternalVideoUrl(src)) {
            return (
              <div key={`video-${index}`} className="my-6 overflow-hidden rounded-lg border border-slate-200 bg-slate-950 shadow-sm">
                <div className="relative aspect-video">
                  <iframe src={buildEmbeddedVideoUrl(src)} title={title} className="absolute inset-0 h-full w-full" allowFullScreen />
                </div>
              </div>
            );
          }
          return (
            <div key={`video-${index}`} className="my-6 overflow-hidden rounded-lg border border-slate-200 bg-slate-950 shadow-sm">
              <video src={src} controls className="aspect-video w-full" />
            </div>
          );
        }

        if (block.type === "gallery") {
          return (
            <div key={`gallery-${index}`} className="my-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {block.items.map((item, itemIndex) => (
                <figure key={itemIndex} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                  <img src={item.src} alt={item.caption || `Gallery image ${itemIndex + 1}`} className="h-56 w-full object-cover" />
                  {item.caption ? <figcaption className="border-t border-slate-100 px-3 py-2 text-sm text-slate-600">{item.caption}</figcaption> : null}
                </figure>
              ))}
            </div>
          );
        }

        if (block.type === "peopleGrid") {
          const entries = getPeopleGridEntries(block.props, people);
          return (
            <div key={`people-${index}`} className="my-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {entries.map((person) => (
                <article key={person.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 overflow-hidden rounded-md bg-slate-100">
                      {person.photoUrl ? (
                        <img src={person.photoUrl} alt={person.name || "Person"} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-400">
                          <User className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-base font-semibold text-slate-900">{person.name || "Unnamed User"}</p>
                      <p className="truncate text-sm text-slate-500">{person.roleTitle || person.title || person.department || person.email || ""}</p>
                    </div>
                  </div>
                </article>
              ))}
              {entries.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                  No people matched this wiki block yet.
                </div>
              ) : null}
            </div>
          );
        }

        if (block.type === "columns") {
          const gridClass =
            block.columns.length >= 3 ? "lg:grid-cols-3" : block.columns.length === 2 ? "md:grid-cols-2" : "grid-cols-1";
          return (
            <div key={`columns-${index}`} className={cn("my-6 grid gap-5", gridClass)}>
              {block.columns.map((column, columnIndex) => (
                <div key={columnIndex} className="min-w-0">
                  <WikiRenderer
                    content={column.content}
                    people={people}
                    pageSlugMap={pageSlugMap}
                    onNavigateToPage={onNavigateToPage}
                    className="prose-sm"
                  />
                </div>
              ))}
            </div>
          );
        }

        if (block.type === "cards") {
          return (
            <div key={`cards-${index}`} className="my-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {block.cards.map((card, cardIndex) => {
                const href = String(card.props.href || "");
                return (
                  <article key={cardIndex} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-2 flex items-center gap-2">
                      <Video className="h-4 w-4 text-slate-400" />
                      <p className="text-sm font-semibold text-slate-900">{card.props.title || `Card ${cardIndex + 1}`}</p>
                    </div>
                    <div className="text-sm text-slate-600">
                      <WikiRenderer
                        content={card.content}
                        people={people}
                        pageSlugMap={pageSlugMap}
                        onNavigateToPage={onNavigateToPage}
                        className="prose-sm"
                      />
                    </div>
                    {href ? (
                      <div className="mt-3">
                        <a href={href} className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
                          Open
                        </a>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          );
        }

        return null;
      })}
    </div>
  );
}
